import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, MessageSquare, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { apiGet, convertEpochToLocalTime, convertEpochToLocalDate, formatTimeRange, calculateDuration } from '../../utils/api';
import { Button, Card, Spinner, Badge } from '../design-system';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('upcoming'); // upcoming, past, all

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await apiGet(`/.netlify/functions/get-bookings?filter=${filter}`);
      setBookings(data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'accent';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (epoch) => {
    if (!epoch || isNaN(epoch)) {
      return 'Invalid date';
    }
    
    try {
      const date = new Date(epoch * 1000); // Convert epoch to Date
      
      // Validate the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Use utility function for consistent timezone handling
      const localDate = convertEpochToLocalDate(epoch);
      const localTime = convertEpochToLocalTime(epoch);
      return `${localDate} at ${localTime}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
        <h2 className="text-lg font-medium text-gray-900">Bookings</h2>
        <div className="flex space-x-1 md:space-x-2 overflow-x-auto pb-2">
          <Button
            onClick={() => setFilter('upcoming')}
            variant={filter === 'upcoming' ? 'primary' : 'ghost'}
            size="small"
            className="flex-shrink-0"
          >
            Upcoming
          </Button>
          <Button
            onClick={() => setFilter('past')}
            variant={filter === 'past' ? 'primary' : 'ghost'}
            size="small"
            className="flex-shrink-0"
          >
            Past
          </Button>
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'primary' : 'ghost'}
            size="small"
            className="flex-shrink-0"
          >
            All
          </Button>
        </div>
      </div>

      {/* Bookings List */}
      <Card>
        <div className="px-4 md:px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {filter === 'upcoming' ? 'Upcoming' : filter === 'past' ? 'Past' : 'All'} Bookings
          </h3>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <Spinner size="medium" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No bookings found</p>
            <p className="text-sm text-gray-400 mt-1">
              {filter === 'upcoming' 
                ? 'No upcoming appointments' 
                : filter === 'past' 
                ? 'No past appointments' 
                : 'No appointments yet'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {bookings.map((booking) => (
              <div key={booking._id} className="px-4 md:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)' }}>
                        <User className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {booking.patientName || 'Anonymous Patient'}
                        </p>
                        <div className="flex items-center space-x-1 text-sm text-gray-500">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{booking.patientEmail}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-sm text-gray-600 mb-2">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDateTime(booking.startEpoch)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{booking.duration} minutes</span>
                      </div>
                    </div>

                    {booking.message && (
                      <div className="flex items-start space-x-2 text-sm text-gray-600">
                        <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <p className="italic break-words">"{booking.message}"</p>
                      </div>
                    )}
                    {booking.meetLink && booking.status === 'confirmed' && (
                      <div className="mt-2">
                        <a
                          href={booking.meetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Join Google Meet
                        </a>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-start sm:justify-end">
                    <Badge variant={getStatusVariant(booking.status)}>
                      {booking.status}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Bookings; 